import { useContext } from 'react'
import swal from 'sweetalert2'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import useProviderPriceList, { useProviderPriceListForm } from '@hooks/useProviderPriceList'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'ArchiveProviderPriceListModal'

type ModalCallbacks = {
  closeModal: () => void,
  archiveProviderPriceList: (payload: HandleCallbackActionParams) => void,
}

type ConfirmArchiveParams = {
  archiveProviderPriceList: ModalCallbacks['archiveProviderPriceList'],
  archiveProviderPriceListPayload: HandleCallbackActionParams,
}

const confirmArchive = (params: ConfirmArchiveParams) => {
  const { archiveProviderPriceList, archiveProviderPriceListPayload } = params

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, archive it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Archive Price List?',
  }).then(({ value }) => {
    if (value){
      archiveProviderPriceList(archiveProviderPriceListPayload)
    }
  })
}

const customRequiredFields = [
  { key: 'next_price_list_id' },
]

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ArchiveProviderPriceListModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, archiveProviderPriceList } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData[MODAL_KEY] || {}
  const hasAgencyAssigned = modalPayload?.hasAgencyAssigned

  const { providerPriceList: initProviderPriceList } = modalPayload

  const {
    callbacks: { deleteProviderPriceList: deleteFn },
    deleting,
    providerPriceList,
    providerPriceList: {
      provider_id, service_id,
    },
  } = useProviderPriceList(initProviderPriceList)

  const formPayload = useProviderPriceListForm(providerPriceList, { customRequiredFields, validateOn: 'change' })

  const {
    entityState,
    entityState: { next_price_list_id },
    errors,
    saveEnabled,
    setEntityState,
  } = formPayload

  const archiveProviderPriceListPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_provider_price_list_agency_providers: true,
      include_provider_price_list_next_price_list: true,
    },
    toastText: 'Price List Archived',
  }

  const noticeMessage = () => {
    if (hasAgencyAssigned){
      return {
        title: 'Reassign Agencies and Archive',
        // eslint-disable-next-line max-len
        paragraph: 'All agencies currently assigned to this price list will be reassigned to the price list selected below.',
      }
    }
    return {
      title: 'No Agencies Assigned',
      paragraph: 'This price list has no agencies assigned to it. You can archive it without reassigning agencies.',
    }
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Archive" titleSecondLine="Price List" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>{noticeMessage().title}</SidebarNoticeBox.Title>
          <SidebarNoticeBox.Paragraph>
            {noticeMessage().paragraph}
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        {hasAgencyAssigned && (
          <Form>
            <EntitySelector
              callbacks={{
                selectItem: priceList => setEntityState({
                  next_price_list_id: priceList ? priceList.id : undefined,
                }),
              }}
              entityKey="providerPriceLists"
              errorMessage={errors.next_price_list_id}
              searchRequestOptions={{
                hidden: false,
                provider_id,
                service_id,
              }}
              selectedIds={[providerPriceList.id]}
              selectedItemId={next_price_list_id}
            />
          </Form>
        )}

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryDestroy"
          disabled={hasAgencyAssigned && !saveEnabled}
          loading={deleting}
          // @ts-expect-error TS2322
          onClick={() => confirmArchive({ archiveProviderPriceList, archiveProviderPriceListPayload })}
          size="large"
        >
          { (!hasAgencyAssigned || saveEnabled) ? 'Archive Price List' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ArchiveProviderPriceListModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
